/**
 * Builds AG Grid column definitions for one section of a headerTree (EE/HCTR) page.
 *
 * Walks a `PageLayoutSection`'s `columnHeaderTree` (GROUP -> BLOCK -> LEAF) into nested
 * `ColGroupDef`/`ColDef`s, joining each leaf to the section's flat `columns[]` projection by `colKey`
 * for its per-column data (format, calculation, excelLetter). Consumed per section by SectionGrid.
 *
 * Scope: column STRUCTURE and field bindings only. Row values come from getDecklines.
 */
import { ColDef, ColGroupDef } from '@amzn/gft-ag-grid-enterprise';
import FootnoteHeaderRenderer, {
  FootnoteHeaderParams,
} from '@deckManagement/components/Page/HeaderComponents/FootnoteHeaderRenderer';
import {
  ColumnCalculationType,
  HeaderPlacement,
  PageScaleValues,
} from '@services/api/deckManagement/graphql/schema/API';
import { cellFormatOverrideFor, formatCellValue, resolveCellFormat } from '@utils/formatResolver';
import { isBlockNode, isGroupNode, isLeafNode } from '@utils/headerNodeShape';
import { isNil } from 'lodash';
import { useMemo } from 'react';
import { BlockNode, ColumnLeaf, DecklineRowData, HeaderNode, LeafNode, PageLayoutSection } from 'types/Page.types';

/**
 * CSS class applied to CPH (cost-per-head) columns, which are read-only computed markers in
 * Sep 2026 (C7). Styling lives in SectionGrid.scss (G1).
 */
export const CPH_CELL_CLASS = 'cph-read-only-cell';

/**
 * Builds an O(1) `colKey -> ColumnLeaf` lookup from a section's flat `columns` projection.
 *
 * The header tree gives grouping structure + labels; the flat `columns[]` carries the per-column
 * ColDef data (format, calculation, excelLetter). `buildColumnDef` joins the two by `colKey`.
 * Null array entries (the schema types `columns` as `(ColumnLeaf | null)[]`) are skipped;
 * `ColumnLeaf.colKey` is non-null so it is always a valid key.
 */
export const buildLeafMap = (columns: PageLayoutSection['columns']): Record<string, ColumnLeaf> => {
  const leafMap: Record<string, ColumnLeaf> = {};
  for (const column of columns ?? []) {
    if (column) {
      leafMap[column.colKey] = column;
    }
  }
  return leafMap;
};

// Node discrimination (`__typename` first, `kind` as the fallback) lives in `utils/headerNodeShape.ts`, shared
// with the Excel export reader so the grid and the export classify nodes the same way.

/**
 * Flattens a section's `columnHeaderTree` to the leaf-column count of each BLOCK, in order.
 *
 * The tree nests GROUP -> BLOCK -> LEAF, but roots may be either GROUP or BLOCK
 * (`HeaderRootNode = GroupNode | BlockNode`). This descends any GROUP root into its BLOCK children so
 * the result is always "one entry per BLOCK = that block's leaf count" — e.g. an FX-Summary section
 * (`[GROUP[BLOCK, BLOCK, BLOCK]]`, 4 leaves each) yields `[4, 4, 4]`, NOT `[3]`.
 *
 * This is the shape the Excel export's spacer-column logic expects (a spacer between each block), so
 * it must count leaves per block, not blocks per group. Discrimination uses the same
 * `resolveNodeShape` (`__typename`, `kind` fallback) as the column builder, so the two never drift.
 */
export const getSectionBlockLeafCounts = (section: PageLayoutSection): number[] => {
  const countLeaves = (block: BlockNode): number =>
    (block.children ?? []).filter((child): child is LeafNode => !isNil(child)).length;

  return (section.columnHeaderTree ?? [])
    .filter((node): node is NonNullable<typeof node> => !isNil(node))
    .flatMap((root) => {
      if (isGroupNode(root)) {
        return (root.children ?? [])
          .filter((block): block is BlockNode => !isNil(block) && isBlockNode(block))
          .map(countLeaves);
      }
      if (isBlockNode(root)) {
        return [countLeaves(root)];
      }
      return [];
    });
};

/**
 * The AG Grid row field key a leaf's cell values bind to.
 *
 * Row values (from getDecklines, assembled by the row-data layer) are keyed by
 * `${excelLetter}_${grain}`, both lowercased — e.g. `a_mtd` — NOT by colKey. `colKey` is the stable
 * column identity (`colId`); `excelLetter` is the positional value key. See EPIC_G_CONTEXT §13.5.
 * Returns undefined when the leaf has no excelLetter (structural-only node).
 */
const toFieldKey = (excelLetter: string | null | undefined, grain: string): string | undefined =>
  // BOTH halves are lowercased. The legacy row builder lowercases the granularity when it writes
  // these keys (Page.tsx), so interpolating the grain raw here would produce `a_MTD` against a row
  // key of `a_mtd` — headers would render with every cell blank and no test would fail. Lowercasing
  // inside this helper makes it caller-proof rather than relying on callers to pre-normalise.
  excelLetter ? `${excelLetter.toLowerCase()}_${grain.toLowerCase()}` : undefined;

/**
 * Builds the ColGroupDef shared by GROUP and BLOCK nodes, recursing into children.
 */
const buildGroupDef = (
  headerName: string,
  children: (HeaderNode | null)[] | null | undefined,
  footnotes: (number | null)[] | null | undefined,
  leafMap: Record<string, ColumnLeaf>,
  grain: string,
  onFootnoteClick?: (label: string, footnotes: number[]) => void,
  pageScale?: PageScaleValues | null,
): ColGroupDef<DecklineRowData> => {
  const headerParams: FootnoteHeaderParams = { footnotes, onFootnoteClick };
  return {
    headerName,
    headerGroupComponent: FootnoteHeaderRenderer,
    headerGroupComponentParams: headerParams,
    children: (children ?? [])
      .map((child) => buildColumnDef(child, leafMap, grain, onFootnoteClick, pageScale))
      .filter((child): child is ColDef<DecklineRowData> | ColGroupDef<DecklineRowData> => child !== null),
  };
};

/**
 * Recursively maps a header-tree node to an AG Grid column definition:
 * - GROUP / BLOCK  -> ColGroupDef (recurses into children)
 * - LEAF           -> ColDef (binds a single column)
 *
 * Nodes are discriminated via {@link resolveNodeShape} (`__typename`, falling back to `kind`) — see
 * that function for why the fallback exists. Generated node fields are deeply-optional, so every
 * access is null-narrowed; malformed/unknown nodes yield `null`.
 *
 * @param node    a header-tree node (GROUP/BLOCK/LEAF)
 * @param leafMap colKey -> ColumnLeaf, from {@link buildLeafMap}, for per-leaf ColDef data
 * @param grain   the page grain (MTD/QTD/YTD), used to build the leaf's row-value field key
 */
export const buildColumnDef = (
  node: HeaderNode | null,
  leafMap: Record<string, ColumnLeaf>,
  grain: string,
  onFootnoteClick?: (label: string, footnotes: number[]) => void,
  pageScale?: PageScaleValues | null,
): ColDef<DecklineRowData> | ColGroupDef<DecklineRowData> | null => {
  if (!node) {
    return null;
  }

  if (isLeafNode(node)) {
    // Join to the flat columns[] projection by colKey for the per-column data (format,
    // calculation, excelLetter). Fall back to the node's own fields when absent.
    const colKey = node.colKey ?? undefined;
    const leaf = colKey ? leafMap[colKey] : undefined;

    const excelLetter = leaf?.excelLetter ?? node.excelLetter;
    const columnDefault = (leaf?.format ?? node.format)?.cellDefault;
    const calculation = leaf?.calculation ?? node.calculation;
    const footnotes = leaf?.footnotes ?? node.footnotes;

    const headerParams: FootnoteHeaderParams = { footnotes, onFootnoteClick };

    const columnDef: ColDef<DecklineRowData> = {
      colId: colKey,
      // Value keys are dynamic (`a_mtd`, `b_summary`, …) so they cannot appear in DecklineRowData's
      // declared keys; AG Grid's NestedFieldPaths therefore rejects the plain string.
      field: toFieldKey(excelLetter, grain) as ColDef<DecklineRowData>['field'],
      headerName: node.label ?? leaf?.label ?? '',
      headerComponent: FootnoteHeaderRenderer,
      headerComponentParams: headerParams,
      // A long leaf label wraps like the band labels above it; the leaf row grows to fit it.
      autoHeaderHeight: true,
      // Format cascade, most specific wins. `columnDefault` must NOT be pre-defaulted to NUMBER here:
      // resolveCellFormat selects by PRESENCE, so a non-null tier 1 would permanently shadow tier 2.
      valueFormatter: (params) =>
        formatCellValue(
          params.value,
          resolveCellFormat(
            columnDefault,
            params.data?.rawDecklineData?.format_cell_default,
            cellFormatOverrideFor(params.data?.rawDecklineData?.format_cell_overrides, colKey),
          ),
          pageScale,
        ),
    };

    // CPH columns are read-only computed markers (C7) — flag with a cell class for styling.
    if (calculation?.type === ColumnCalculationType.CPH) {
      columnDef.cellClass = CPH_CELL_CLASS;
    }

    return columnDef;
  }

  // BLOCK band text comes from `label`, NOT `resolvedScenario`: the BE folds the shared year into the
  // label ('2025 Actuals') while resolvedScenario carries only the scenario ('Actuals'). Preferring
  // resolvedScenario silently drops the year, which is visible in the deck. `resolvedScenario` stays
  // as the fallback for a block authored without a label.
  if (isBlockNode(node)) {
    // Standard-P&L / EE style: the block's leaves differ BY SCENARIO (scenarioPlacement = LEAF), so
    // `label` is the MEASURE band ('Amount', 'Variance ex FX H/(L)') — NOT a scenario — and scenario
    // and period each need their own per-column header row. The scenario-BAND path below draws only
    // two rows (scenario band + period/account leaf), which folds a varying scenario and its period
    // into one leaf cell ('Actuals AUG 25') and drops a uniform axis entirely; the deck shows three
    // rows (measure, scenario, period). So wrap each leaf: measure band -> per-column scenario group
    // -> period leaf. resolvedScenario/resolvedPeriod are populated per leaf even for a shared axis,
    // so a uniform value repeats down the column exactly as the deck prints it.
    if (node.scenarioPlacement === HeaderPlacement.LEAF) {
      const scenarioColumns = (node.children ?? [])
        .map((child) => {
          const leafDef = buildColumnDef(child, leafMap, grain, onFootnoteClick, pageScale);
          if (!leafDef || !child || !isLeafNode(child)) {
            return leafDef;
          }
          const joined = child.colKey ? leafMap[child.colKey] : undefined;
          const period = child.resolvedPeriod ?? joined?.scenarioPeriod;
          const scenario = child.resolvedScenario ?? joined?.scenarioDisplay ?? '';
          // The period is the bottom (leaf) row; only override the label when we have one so a leaf
          // that carries its own heading (a dimension name) is left intact.
          if (!isNil(period)) {
            (leafDef as ColDef<DecklineRowData>).headerName = period;
          }
          return {
            headerName: scenario,
            headerGroupComponent: FootnoteHeaderRenderer,
            // Footnotes stay on the leaf; the synthesised scenario row carries none.
            headerGroupComponentParams: { footnotes: null } satisfies FootnoteHeaderParams,
            children: [leafDef],
          } satisfies ColGroupDef<DecklineRowData>;
        })
        .filter((child): child is ColDef<DecklineRowData> | ColGroupDef<DecklineRowData> => child != null);
      return {
        headerName: node.label ?? node.measure ?? '',
        headerGroupComponent: FootnoteHeaderRenderer,
        headerGroupComponentParams: { footnotes: node.footnotes, onFootnoteClick } satisfies FootnoteHeaderParams,
        children: scenarioColumns,
      };
    }

    const scenarioBand = buildGroupDef(
      node.label ?? node.resolvedScenario ?? '',
      node.children,
      node.footnotes,
      leafMap,
      grain,
      onFootnoteClick,
      pageScale,
    );

    // Period placement decides how many header rows a block occupies:
    //
    //  periodPlacement = LEAF  -> the leaves ARE the periods (JUL/AUG/SEP/…), so the scenario band
    //                             sits directly above them. Two rows, nothing to synthesise.
    //                             (Total Summary.)
    //  periodPlacement = BAND  -> the whole block shares one period, drawn on its OWN row beneath
    //                             the scenario band. Three rows from a two-level subtree, so the
    //                             extra row has to be synthesised as an intermediate ColGroupDef —
    //                             AG Grid derives header rows from ColGroupDef nesting depth.
    //                             (FX Summary, where bandPeriod is e.g. 'AUG 26'.)
    //
    // `bandPeriod` is only populated when both axes are BAND; `resolvedPeriod` is the defensive
    // fallback for a BAND period that arrives without it.
    const periodBandLabel = node.bandPeriod ?? node.resolvedPeriod;
    if (node.periodPlacement === HeaderPlacement.BAND && !isNil(periodBandLabel)) {
      return {
        ...scenarioBand,
        children: [
          {
            headerName: periodBandLabel,
            headerGroupComponent: FootnoteHeaderRenderer,
            // Footnotes belong to the scenario band, not to the synthesised period row.
            headerGroupComponentParams: { footnotes: null } satisfies FootnoteHeaderParams,
            children: scenarioBand.children,
          },
        ],
      };
    }

    return scenarioBand;
  }
  if (isGroupNode(node)) {
    return buildGroupDef(node.label ?? '', node.children, node.footnotes, leafMap, grain, onFootnoteClick, pageScale);
  }

  return null;
};

/**
 * useHeaderTreeColumns
 *
 * Builds the AG Grid column definitions for one headerTree (EE/HCTR) page section. Walks the
 * section's pre-structured `columnHeaderTree` (GROUP -> BLOCK -> LEAF) into a `(ColGroupDef |
 * ColDef)[]`, joining each leaf to the flat `columns[]` projection by colKey for its ColDef data.
 *
 * Scope (Epic G / G2, Option A): produces column STRUCTURE + field bindings only. Row values are
 * supplied by the getDecklines/row-data layer (G1), keyed by `${excelLetter}_${grain}`. Called per
 * section by SectionGrid.
 *
 * @param section a PageLayoutSection from getPageLayout
 * @param grain   the page grain (MTD/QTD/YTD), used to build each leaf's row-value field key
 * @returns the section's top-level AG Grid column definitions, memoized on [section, grain]
 */
export const useHeaderTreeColumns = (
  section: PageLayoutSection,
  grain: string,
  onFootnoteClick?: (label: string, footnotes: number[]) => void,
  pageScale?: PageScaleValues | null,
): (ColDef<DecklineRowData> | ColGroupDef<DecklineRowData>)[] =>
  useMemo(() => {
    const leafMap = buildLeafMap(section.columns);
    const roots = (section.columnHeaderTree ?? [])
      .map((node) => buildColumnDef(node, leafMap, grain, onFootnoteClick, pageScale))
      .filter((def): def is ColDef<DecklineRowData> | ColGroupDef<DecklineRowData> => def !== null);

    // A titled section draws its label as a band spanning ALL of its data columns, above the block
    // bands (the deck shows `Amount` merged across C:X, over the four scenario blocks). Wrapping the
    // roots in one ColGroupDef is what produces that row, since AG Grid derives header rows from
    // nesting depth. An untitled section (FX Summary, sectionId and label both null) gets no wrapper —
    // there its top band is a GROUP node inside the tree instead.
    if (isNil(section.label) || section.label === '') {
      return roots;
    }
    return [
      {
        headerName: section.label,
        headerGroupComponent: FootnoteHeaderRenderer,
        headerGroupComponentParams: { footnotes: null } satisfies FootnoteHeaderParams,
        children: roots,
      },
    ];
  }, [section, grain, onFootnoteClick, pageScale]);

export default useHeaderTreeColumns;
